import { useMemo } from 'react'

import { diffWords } from '../utils/textDiff'
import '../../workspace/css/workspace.scss'

// Display pieces for adapter-added replay state (see utils/replayAnnotations.js). Regular
// workspace replays never have this state, so none of these render for them.

const NOTICE_TEXT = { fontFamily: "'Open Sans', sans-serif", fontSize: '0.875rem', lineHeight: 1.5, color: '#171717' }

function NoticeDetails ({ notice }) {
  return (
    <>
      {(notice.lines || []).map((line, i) => (
        <div key={i} style={{ color: '#525252' }}>{line}</div>
      ))}
      {notice.items?.length > 0 && (
        <ol style={{ margin: '0.5rem 0 0', paddingLeft: '20px' }}>
          {notice.items.map((item, i) => <li key={i} style={{ margin: '4px 0' }}>{item}</li>)}
        </ol>
      )}
    </>
  )
}

// 'screen': the student was on another screen (e.g. feedback), so it covers the editor.
// 'banner': shown above the editor and collapsible, as on the student's page.
export function ReplayNotice ({ notice }) {
  if (!notice) return null
  if (notice.mode === 'banner') {
    return (
      <details open style={{ ...NOTICE_TEXT, flexShrink: 0, maxHeight: '30%', overflowY: 'auto', padding: '0.5rem 1rem', background: '#eef4ff', borderBottom: '1px solid #e5e5e5' }}>
        <summary style={{ fontWeight: 700, cursor: 'pointer' }}>{notice.title}</summary>
        <div style={{ marginTop: '0.3rem' }}><NoticeDetails notice={notice} /></div>
      </details>
    )
  }
  return (
    <div style={{ position: 'absolute', inset: 0, zIndex: 35, overflowY: 'auto', background: 'rgba(245, 245, 245, 0.97)', padding: '2rem 1.5rem' }}>
      <div style={{ ...NOTICE_TEXT, maxWidth: '640px', margin: '0 auto', padding: '1.25rem 1.5rem', background: '#ffffff', border: '1px solid #e5e5e5', borderRadius: '0.5rem' }}>
        <div style={{ fontSize: '0.72rem', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#737373', marginBottom: '0.5rem' }}>
          Student's screen
        </div>
        <div style={{ fontWeight: 700, marginBottom: '0.4rem' }}>{notice.title}</div>
        <NoticeDetails notice={notice} />
      </div>
    </div>
  )
}

const ADDED = { background: 'rgba(34, 197, 94, 0.22)', borderRadius: '2px' }
const REMOVED = { background: 'rgba(239, 68, 68, 0.15)', color: '#b91c1c', textDecoration: 'line-through', borderRadius: '2px' }

// before: the explanation just before the latest edit, to mark what that edit added and
// removed; null shows the text plainly.
function ExplanationText ({ text, before }) {
  const ops = useMemo(() => (before == null ? null : diffWords(before, text)), [before, text])
  if (!ops) return text
  return ops.map((op, i) => (
    op.type === 'same' ? op.value : <span key={i} style={op.type === 'add' ? ADDED : REMOVED}>{op.value}</span>
  ))
}

export function ReplayExplanation ({ prompt, text, before = null, caption = null }) {
  return (
    <div style={{ flex: '0 0 40%', minHeight: '120px', display: 'flex', flexDirection: 'column', padding: '0.6rem 1rem 0.75rem', borderTop: '1px solid #e5e5e5', background: '#ffffff' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '0.5rem' }}>
        <span className="ws-section-title" style={{ margin: '0 0 0.25rem' }}>Explain your thinking</span>
        {caption && <span style={{ fontSize: '0.75rem', color: '#737373' }}>{caption}</span>}
      </div>
      {prompt && (
        <div title={prompt} style={{ fontSize: '0.8rem', color: '#737373', marginBottom: '0.4rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {prompt}
        </div>
      )}
      <div style={{ ...NOTICE_TEXT, flex: 1, minHeight: 0, overflowY: 'auto', whiteSpace: 'pre-wrap', padding: '0.6rem 0.75rem', border: '1px solid #e5e5e5', borderRadius: '0.375rem', background: '#fafafa' }}>
        {text || before
          ? <ExplanationText text={text || ''} before={before} />
          : <span style={{ color: '#a3a3a3', fontStyle: 'italic' }}>Nothing written yet.</span>}
      </div>
    </div>
  )
}

const AWAY_REASONS = {
  tab: 'Student away: this tab was hidden',
  window: 'Student away: another window was in front',
}

export function ReplayAwayOverlay ({ away }) {
  if (!away) return null
  return (
    <div style={{ position: 'absolute', inset: 0, zIndex: 9000, pointerEvents: 'none', background: 'rgba(17, 24, 39, 0.28)', display: 'flex', justifyContent: 'center', alignItems: 'flex-start' }}>
      <div style={{ ...NOTICE_TEXT, marginTop: '12px', padding: '6px 14px', borderRadius: '999px', background: '#111827', color: '#ffffff', fontWeight: 600, boxShadow: '0 2px 8px rgba(0,0,0,0.25)' }}>
        {AWAY_REASONS[away.reason] || 'Student away from the page'}
      </div>
    </div>
  )
}
